/**
 * Media Display Helpers
 * 
 * Utility functions for media display logic (movies and TV shows)
 * Extracted from media-display.jsx for reusability
 */

import { GENRE_NAMES } from '../constants';

/**
 * Get rating data from enhanced ratings or TMDB
 * @param {Object} media - Movie or TV show object
 * @returns {Object} Rating data with imdb, rt, tmdb, and hasRealData flags
 */
export function getRatingData(media) {
  const data = {
    imdb: null,
    rt: null,
    metacritic: null,
    userRating: null, // User community rating (TMDB/MDBList)
    hasRealData: false
  };

  // Get real ratings (IMDB, RT, and Metacritic)
  if (media.enhanced_ratings?.real_ratings) {
    data.hasRealData = true;
    if (media.enhanced_ratings.real_ratings.imdb?.rating) {
      data.imdb = media.enhanced_ratings.real_ratings.imdb.rating.toFixed(1);
    }
    if (media.enhanced_ratings.real_ratings.rottenTomatoes?.rating) {
      data.rt = media.enhanced_ratings.real_ratings.rottenTomatoes.rating;
    }
    if (media.enhanced_ratings.real_ratings.metacritic?.rating) {
      data.metacritic = media.enhanced_ratings.real_ratings.metacritic.rating;
    }
  }
  
  const hasImdb = data.imdb !== null && data.imdb !== undefined;
  const hasRt = data.rt !== null && data.rt !== undefined;
  const hasMetacritic = data.metacritic !== null && data.metacritic !== undefined;
  
  // Get user rating value (from vote_average which comes from TMDB/MDBList)
  let userRatingValue = null;
  if (media.vote_average && media.vote_average > 0) {
    userRatingValue = media.vote_average.toFixed(1);
  } else if (media.enhanced_ratings?.tmdb?.rating && media.enhanced_ratings.tmdb.rating > 0) {
    userRatingValue = media.enhanced_ratings.tmdb.rating.toFixed(1);
  }
  
  // Strategy: Always try to show exactly 2 ratings (max)
  // Priority: IMDB > RT > Metacritic > User Rating
  
  if (hasImdb && hasRt) {
    // Best case: IMDB + RT available, don't show anything else
    data.metacritic = null;
  } else if (hasImdb && !hasRt) {
    // Has IMDB but no RT
    if (!hasMetacritic && userRatingValue) {
      // Show IMDB + User Rating (2 ratings)
      data.userRating = userRatingValue;
    }
  } else if (!hasImdb && hasRt) {
    // Has RT but no IMDB
    if (!hasMetacritic && userRatingValue) {
      // Show RT + User Rating (2 ratings)
      data.userRating = userRatingValue;
    }
  } else if (!hasImdb && !hasRt && hasMetacritic) {
    // Has only Metacritic, add User Rating as second
    if (userRatingValue) {
      data.userRating = userRatingValue;
    }
  } else if (!hasImdb && !hasRt && !hasMetacritic && userRatingValue) {
    // No professional ratings: show only User Rating
    data.userRating = userRatingValue;
  }

  return data;
}

/**
 * Get year from date string
 * @param {string} dateString - ISO date string
 * @returns {number|string} Year or 'TBA' if no date
 */
export function getYear(dateString) {
  return dateString ? new Date(dateString).getFullYear() : 'TBA';
}

/**
 * Get genre names from genre IDs using shared constants
 * @param {Array<number>} genreIds - Array of TMDB genre IDs
 * @returns {Array<string>} Array of genre names (max 2)
 */
export function getGenreNames(genreIds) {
  if (!genreIds || !Array.isArray(genreIds)) return [];
  return genreIds.slice(0, 2).map(id => GENRE_NAMES[id] || 'Nežinomas');
}

/**
 * Format runtime to hours and minutes
 * @param {number} minutes - Runtime in minutes
 * @returns {string} Formatted runtime (e.g., "2h 30m")
 */
export function formatRuntime(minutes) {
  if (!minutes) return '';
  const hours = Math.floor(minutes / 60);
  const mins = minutes % 60;
  return hours > 0 ? `${hours}h ${mins}m` : `${mins}m`;
}

/**
 * Get vote count display string
 * @param {number} voteCount - Number of votes
 * @returns {string} Formatted vote count (e.g., "1.2K", "15K")
 */
export function formatVoteCount(voteCount) {
  if (!voteCount) return '0';
  if (voteCount >= 1000) {
    return `${(voteCount / 1000).toFixed(1)}K`;
  }
  return voteCount.toString();
}

/**
 * Check if media has real ratings (IMDB or RT)
 * @param {Object} media - Movie or TV show object
 * @returns {boolean} True if has real ratings
 */
export function hasRealRatings(media) {
  if (!media?.enhanced_ratings?.real_ratings) return false;
  const { imdb, rottenTomatoes } = media.enhanced_ratings.real_ratings;
  return !!(imdb?.rating || rottenTomatoes?.rating);
}

/**
 * Get the primary rating to display
 * @param {Object} media - Movie or TV show object
 * @returns {Object|null} { value, source, isPercentage } or null
 */
export function getPrimaryRating(media) {
  const ratingData = getRatingData(media);
  
  if (ratingData.imdb) {
    return {
      value: ratingData.imdb,
      source: 'IMDB',
      isPercentage: false
    };
  }
  
  if (ratingData.rt) {
    return {
      value: ratingData.rt,
      source: 'RT',
      isPercentage: true
    };
  }
  
  if (ratingData.metacritic) {
    return {
      value: ratingData.metacritic,
      source: 'Metacritic',
      isPercentage: false
    };
  }
  
  if (ratingData.userRating) {
    return {
      value: ratingData.userRating,
      source: 'Users',
      isPercentage: false
    };
  }
  
  return null;
}
